import {
  KeyboardEvent as ReactKeyboardEvent,
  RefObject,
  SyntheticEvent,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import {
  getGhostCompletion,
  isGhostAcceptKey,
  isImeKeyEvent,
} from '../HeaderSearch/ghostCompletion';

export type UseGhostCompletionOptions = {
  inputRef: RefObject<HTMLInputElement | null>;
  value: string;
  suggestion?: string;
  enabled: boolean;
};

export type GhostCompletion = {
  completion: string;
  inputProps: {
    onCompositionStart: () => void;
    onCompositionEnd: () => void;
    onSelect: (e: SyntheticEvent<HTMLInputElement>) => void;
  };
  shouldAccept: (e: ReactKeyboardEvent<HTMLInputElement>) => boolean;
};

const isCaretAtEnd = (input: HTMLInputElement, length: number) =>
  input.selectionStart === length && input.selectionEnd === length;

const isOverflowing = (input: HTMLInputElement) =>
  input.scrollWidth > input.clientWidth;

export const useGhostCompletion = ({
  inputRef,
  value,
  suggestion,
  enabled,
}: UseGhostCompletionOptions): GhostCompletion => {
  const [composing, setComposing] = useState(false);
  const [caretAtEnd, setCaretAtEnd] = useState(true);
  const [overflowing, setOverflowing] = useState(false);

  // A controlled value change can move the caret without firing onSelect, and
  // once the text scrolls inside the input an overlay can no longer line up
  // with it, so both are re-read after every value change. The text can also
  // start scrolling with no keystroke at all — a narrower viewport, a zoom —
  // so overflow is re-read whenever the input resizes too.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    setOverflowing(isOverflowing(input));
    setCaretAtEnd(isCaretAtEnd(input, value.length));
  }, [inputRef, value]);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }
    const observer = new ResizeObserver(() =>
      setOverflowing(isOverflowing(input))
    );
    observer.observe(input);
    return () => observer.disconnect();
  }, [inputRef]);

  const completion =
    enabled && !composing && caretAtEnd && !overflowing
      ? getGhostCompletion(value, suggestion)
      : '';

  return {
    completion,
    inputProps: {
      onCompositionStart: () => setComposing(true),
      onCompositionEnd: () => setComposing(false),
      onSelect: (e) =>
        setCaretAtEnd(
          isCaretAtEnd(e.currentTarget, e.currentTarget.value.length)
        ),
    },
    shouldAccept: (e) =>
      completion !== '' &&
      !isImeKeyEvent(e.nativeEvent) &&
      isGhostAcceptKey(e, caretAtEnd),
  };
};
